import { useMemo } from "react";

import { EmptystateStacksColored } from "components/icons/generated";
import EmptyState from "ds/components/EmptyState";
import { PolicyIntentProjectAttachment, PolicyStackAttachment } from "types/generated";
import ListEntitiesNew from "components/ListEntitiesNew";
import { decodeURIParam } from "utils/urls";
import useURLParams from "hooks/useURLParams";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import useTitle from "hooks/useTitle";

import PolicyDetailsUsedByVirtualizedListItem from "./Virtualized";
import PolicyDetailsUsedByFiltersLayout from "./FiltersLayout";

type PolicyDetailsUsedByProps = {
  name: string;
  attachedItems: (PolicyStackAttachment | PolicyIntentProjectAttachment)[];
  loading: boolean;
};

const PolicyDetailsUsedBy = ({ name, attachedItems, loading }: PolicyDetailsUsedByProps) => {
  const urlParams = useURLParams();

  const querySearch = decodeURIParam(urlParams.get(URL_SEARCH_KEY));

  useTitle(`Used by · ${name}`);

  const filteredAttachedItems = useMemo(() => {
    return querySearch
      ? attachedItems.filter((item) =>
          "stackName" in item
            ? item.stackName.toLowerCase().includes(querySearch.toLowerCase())
            : item.projectName.toLowerCase().includes(querySearch.toLowerCase())
        )
      : attachedItems;
  }, [attachedItems, querySearch]);

  return (
    <PolicyDetailsUsedByFiltersLayout
      isPageEmpty={!filteredAttachedItems.length && !querySearch}
      renderedEntitiesCount={filteredAttachedItems.length}
      loadingIndication={loading}
      // TODO: [Policy library] waiting for backend
      filteredCount={attachedItems.length}
    >
      {!filteredAttachedItems.length && !querySearch && (
        <EmptyState
          title="There are no stacks or modules that use this policy yet"
          caption="Attach this policy to stack or module to start using it."
          icon={EmptystateStacksColored}
        />
      )}
      {!filteredAttachedItems.length && querySearch && (
        <EmptyState
          title="No results"
          icon={EmptystateStacksColored}
          caption="Try different filters."
          announce
        />
      )}
      {!!filteredAttachedItems.length && (
        <ListEntitiesNew
          itemCount={filteredAttachedItems.length}
          itemProps={{
            items: filteredAttachedItems,
          }}
          virtualizedItem={PolicyDetailsUsedByVirtualizedListItem}
          itemKey={(index) => filteredAttachedItems[index].id}
        />
      )}
    </PolicyDetailsUsedByFiltersLayout>
  );
};

export default PolicyDetailsUsedBy;
